import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, Award, Clock, CheckCircle, Star, TrendingUp, AlertTriangle, Calendar, Timer, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const getTierConfig = (tier) => {
  const tiers = {
    'platinum': { label: 'Platinum', color: 'bg-gradient-to-r from-slate-400 to-slate-600', textColor: 'text-slate-700', icon: '💎', description: 'Elite performer' },
    'gold': { label: 'Gold', color: 'bg-gradient-to-r from-amber-400 to-amber-600', textColor: 'text-amber-700', icon: '🥇', description: 'Top performer' },
    'silver': { label: 'Silver', color: 'bg-gradient-to-r from-slate-300 to-slate-500', textColor: 'text-slate-600', icon: '🥈', description: 'Consistent performer' },
    'bronze': { label: 'Bronze', color: 'bg-gradient-to-r from-orange-300 to-orange-500', textColor: 'text-orange-700', icon: '🥉', description: 'Building track record' },
    'new': { label: 'New', color: 'bg-gradient-to-r from-cyan-400 to-cyan-600', textColor: 'text-cyan-700', icon: '✨', description: 'Just getting started' }
  };
  return tiers[tier] || tiers['new'];
};

const StatCard = ({ icon: Icon, label, value, subValue, color = 'cyan' }) => (
  <div className="bg-white rounded-lg border p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 bg-${color}-100 rounded-full flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
        {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
      </div>
    </div>
  </div>
);

const RatingStars = ({ rating, size = 'md' }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const sizeClass = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`${sizeClass} ${i < fullStars ? 'fill-amber-400 text-amber-400' : i === fullStars && hasHalf ? 'fill-amber-400/50 text-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
};

const TalentScorecard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scorecard, setScorecard] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchScorecard();
    fetchRatings();
  }, []);

  const fetchScorecard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/performance/scorecard/me');
      setScorecard(response.data);
    } catch (error) {
      console.error('Failed to load scorecard:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    try {
      const response = await api.get('/performance/ratings/received?limit=10');
      setRatings(response.data);
    } catch (error) {
      console.error('Failed to load ratings:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const tierConfig = getTierConfig(scorecard?.tier_badge);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="talent-scorecard-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tier Badge Header */}
        <div className={`${tierConfig.color} rounded-xl p-6 md:p-8 text-white mb-8`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="text-5xl">{tierConfig.icon}</div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Performance Scorecard</h1>
                <p className="text-white/80">{tierConfig.description}</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <Badge className="bg-white/20 text-white text-lg px-4 py-2" data-testid="tier-badge">
                {tierConfig.label} Tier
              </Badge>
            </div>
          </div>
          
          {/* Quick Stats Row */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/20">
            <div className="text-center">
              <p className="text-3xl font-bold" data-testid="total-gigs">{scorecard?.reliability?.total_gigs_completed || 0}</p>
              <p className="text-white/80 text-sm">Gigs Completed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold" data-testid="avg-rating">{scorecard?.ratings?.avg_overall?.toFixed(1) || '0.0'}</p>
              <p className="text-white/80 text-sm">Avg Rating</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold" data-testid="total-hours">{scorecard?.summary?.total_hours_worked?.toFixed(0) || 0}</p>
              <p className="text-white/80 text-sm">Hours Worked</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-white">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="metrics" data-testid="metrics-tab">Detailed Metrics</TabsTrigger>
            <TabsTrigger value="ratings" data-testid="ratings-tab">Ratings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Reliability Section */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-500" />
                  Reliability Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Completion Rate</span>
                      <span className="text-sm font-bold text-cyan-600" data-testid="completion-rate">
                        {scorecard?.reliability?.completion_rate?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <Progress value={scorecard?.reliability?.completion_rate || 0} className="h-3" />
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                      <p className="text-lg font-bold text-green-600">{scorecard?.reliability?.total_gigs_completed || 0}</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                      <p className="text-lg font-bold text-amber-600">{scorecard?.reliability?.total_cancelled || 0}</p>
                      <p className="text-xs text-muted-foreground">Cancelled</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <Users className="w-5 h-5 text-red-600 mx-auto mb-1" />
                      <p className="text-lg font-bold text-red-600">{scorecard?.reliability?.total_no_shows || 0}</p>
                      <p className="text-xs text-muted-foreground">No Shows</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Punctuality Section */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-500" />
                  Punctuality Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">On-Time Rate</span>
                      <span className="text-sm font-bold text-purple-600" data-testid="punctuality-rate">
                        {scorecard?.punctuality?.punctuality_rate?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <Progress value={scorecard?.punctuality?.punctuality_rate || 0} className="h-3 [&>div]:bg-purple-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-lg font-bold text-green-600">{scorecard?.punctuality?.on_time_checkins || 0}</p>
                      <p className="text-xs text-muted-foreground">On-Time Check-ins</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-lg font-bold text-amber-600">{scorecard?.punctuality?.late_checkins || 0}</p>
                      <p className="text-xs text-muted-foreground">Late Check-ins</p>
                    </div>
                  </div>
                  {scorecard?.punctuality?.avg_variance_minutes !== 0 && (
                    <p className="text-sm text-muted-foreground text-center">
                      Average variance: {Math.abs(scorecard?.punctuality?.avg_variance_minutes || 0).toFixed(0)} minutes {scorecard?.punctuality?.avg_variance_minutes > 0 ? 'late' : 'early'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Rating Summary */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Rating Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-4xl font-bold" data-testid="overall-rating">
                    {scorecard?.ratings?.avg_overall?.toFixed(1) || '0.0'}
                  </div>
                  <div>
                    <RatingStars rating={scorecard?.ratings?.avg_overall || 0} size="lg" />
                    <p className="text-sm text-muted-foreground mt-1">
                      Based on {scorecard?.ratings?.total_ratings || 0} ratings
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {[
                    { label: 'Professionalism', value: scorecard?.ratings?.avg_professionalism },
                    { label: 'Skill', value: scorecard?.ratings?.avg_skill },
                    { label: 'Communication', value: scorecard?.ratings?.avg_communication },
                    { label: 'Appearance', value: scorecard?.ratings?.avg_appearance }
                  ].map((metric) => (
                    <div key={metric.label} className="flex items-center justify-between">
                      <span className="text-sm">{metric.label}</span>
                      <div className="flex items-center gap-2">
                        <RatingStars rating={metric.value || 0} />
                        <span className="text-sm font-medium w-8">{metric.value?.toFixed(1) || '0.0'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Detailed Metrics Tab */}
          <TabsContent value="metrics" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={CheckCircle} label="Total Assigned" value={scorecard?.reliability?.total_gigs_assigned || 0} color="cyan" />
              <StatCard icon={Award} label="Completed" value={scorecard?.reliability?.total_gigs_completed || 0} color="green" />
              <StatCard icon={Timer} label="Hours Worked" value={`${scorecard?.summary?.total_hours_worked?.toFixed(0) || 0}h`} color="purple" />
              <StatCard icon={Star} label="Total Ratings" value={scorecard?.ratings?.total_ratings || 0} color="amber" />
            </div>

            {/* Tier Requirements */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Tier Requirements</CardTitle>
                <CardDescription>What you need to reach the next tier</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { tier: 'Bronze', gigs: 3, completion: 0, punctuality: 0, current: scorecard?.tier_badge === 'bronze' },
                    { tier: 'Silver', gigs: 10, completion: 85, punctuality: 85, current: scorecard?.tier_badge === 'silver' },
                    { tier: 'Gold', gigs: 25, completion: 90, punctuality: 90, current: scorecard?.tier_badge === 'gold' },
                    { tier: 'Platinum', gigs: 50, completion: 95, punctuality: 95, current: scorecard?.tier_badge === 'platinum' }
                  ].map((req) => (
                    <div 
                      key={req.tier} 
                      className={`p-4 rounded-lg border ${req.current ? 'bg-cyan-50 border-cyan-300' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getTierConfig(req.tier.toLowerCase()).icon}</span>
                          <span className="font-medium">{req.tier}</span>
                          {req.current && <Badge className="bg-cyan-500">Current</Badge>}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {req.gigs}+ gigs • {req.completion > 0 ? `${req.completion}% completion` : 'No min'} • {req.punctuality > 0 ? `${req.punctuality}% on-time` : 'No min'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings" className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Recent Ratings</CardTitle>
                <CardDescription>Feedback from clients you've worked with</CardDescription>
              </CardHeader>
              <CardContent>
                {ratings.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-muted-foreground">No ratings yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Complete gigs to receive ratings from clients</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="ratings-list">
                    {ratings.map((rating) => (
                      <div key={rating.id} className="border rounded-lg p-4" data-testid="rating-item">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium">{rating.rater_name}</p>
                            <p className="text-sm text-muted-foreground">{rating.venue_name}</p>
                          </div>
                          <div className="text-right">
                            <RatingStars rating={rating.overall_rating} />
                            <p className="text-xs text-muted-foreground mt-1">
                              {rating.shift_date ? formatDateMonthDay(rating.shift_date) : ''}
                            </p>
                          </div>
                        </div>
                        {rating.comment && (
                          <p className="text-sm text-gray-600 mt-2 italic">"{rating.comment}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TalentScorecard;
